// Pure wording for the Teams billing views (the Renewal column, the Billing
// card and the refresh button), kept out of orgs-list.ts and org-detail.ts so
// it is unit-testable without a DOM (see teams-billing-format.test.ts).
// Nothing here may value-import another project module: node --test cannot
// resolve the project's extension-less imports.
//
// Every input may be missing: an API that predates the renewal and billing
// fields sends neither, and the views must still draw. A missing value is
// null (the view shows a dash or skips the block), never a throw, and a value
// this build does not know is never printed raw unless the comment says so.

import type {
  RefreshResult,
  TeamsBilling,
  TeamsOffer,
  TeamsOfferKind,
  TeamsPrice,
  TeamsRenewalSummary,
  TeamsSubscriptionDetail,
} from '../api/types';

export type Tone = 'neutral' | 'warning';

export interface Labelled {
  text: string;
  tone: Tone;
  /** The full UTC instant behind the date in `text` (a hover); absent when there is no readable date. */
  title?: string;
}

/**
 * `locale` and `timeZone` exist for tests; the viewer's own are used
 * otherwise. `now` decides which dates need their year.
 */
export interface FormatOptions {
  now?: Date;
  locale?: string;
  timeZone?: string;
}

export const REFRESH_BUTTON_LABEL = 'Refresh from Apple & Stripe';
export const NO_PAID_SUBSCRIPTION = 'No paid subscription. Complimentary access or none.';

/**
 * Some ICU builds put U+202F, a narrow no-break space, before AM/PM. It is
 * invisible and does not match a plain space when copied into a ticket.
 */
function plain(text: string): string {
  return text.replace(/\s/g, ' ');
}

function parseDate(value: string | null | undefined): Date | null {
  if (!value) return null;
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? null : date;
}

function yearOf(date: Date, timeZone: string | undefined): string {
  return new Intl.DateTimeFormat('en-US', { year: 'numeric', timeZone }).format(date);
}

/** "Nov 24", or "Nov 24, 2027" when the year is not the current one. */
export function formatDay(
  value: string | null | undefined,
  options: FormatOptions = {},
): string | null {
  const date = parseDate(value);
  if (!date) return null;
  const { locale, timeZone } = options;
  const sameYear = yearOf(date, timeZone) === yearOf(options.now ?? new Date(), timeZone);
  return plain(
    new Intl.DateTimeFormat(locale, {
      month: 'short',
      day: 'numeric',
      ...(sameYear ? {} : { year: 'numeric' }),
      timeZone,
    }).format(date),
  );
}

/** "Oct 20, 2:05 PM", with the year when it is not the current one. */
export function formatDayTime(
  value: string | null | undefined,
  options: FormatOptions = {},
): string | null {
  const date = parseDate(value);
  if (!date) return null;
  const { locale, timeZone } = options;
  const sameYear = yearOf(date, timeZone) === yearOf(options.now ?? new Date(), timeZone);
  return plain(
    new Intl.DateTimeFormat(locale, {
      month: 'short',
      day: 'numeric',
      ...(sameYear ? {} : { year: 'numeric' }),
      hour: 'numeric',
      minute: '2-digit',
      timeZone,
    }).format(date),
  );
}

/**
 * The full instant in UTC, "2026-11-03 18:00:00 UTC". Dates are drawn in the
 * viewer's zone, so one just after UTC midnight reads as the day before west of
 * it; this is what a hover shows to reconcile that with the API's Slack
 * message, which names the UTC day.
 */
export function utcInstant(value: string | null | undefined): string | null {
  const date = parseDate(value);
  return date ? `${date.toISOString().slice(0, 19).replace('T', ' ')} UTC` : null;
}

function dated(prefix: string, value: string | null | undefined, options: FormatOptions): string {
  const day = formatDay(value, options);
  return day ? `${prefix} ${day}` : `${prefix} (date unknown)`;
}

function withTitle(label: Labelled, value: string | null | undefined): Labelled {
  const title = utcInstant(value);
  return title ? { ...label, title } : label;
}

/** The pill classes the two views draw a tone with. */
export function tonePillClass(tone: Tone): string {
  return tone === 'warning' ? 'pill pill-warn' : 'pill';
}

/**
 * The Teams list's Renewal cell. Null means "nothing to say" (no summary, or a
 * state this build does not know): the cell shows a dash.
 *
 * A team in an offer says which: " · trial" for a free trial, " · intro" for
 * any other kind. The Billing card on the team page has the exact offer.
 *
 * A team that would renew but whose payment is failing (`paymentIssue`) is in
 * the warning tone. Apple's reads "Payment failing · <date>": in the billing
 * grace period that date is when access stops, not a renewal. Stripe's reads
 * "Payment failing" with no date, because its date is a period end Stripe has
 * already rolled forward and will not honour if the card stays bad. A sandbox
 * subscription (App Review's) ends in " · sandbox". Both fields are missing
 * from an API that predates them, which reads as false and null.
 */
export function renewalLabel(
  summary: TeamsRenewalSummary | null | undefined,
  options: FormatOptions = {},
): Labelled | null {
  if (!summary) return null;
  if (summary.state !== 'renewing' && summary.state !== 'cancelling') return null;
  const cancelling = summary.state === 'cancelling';
  // A payment that is failing only matters for a team that would otherwise renew.
  const failing = !cancelling && summary.paymentIssue === true;
  const stripeFailing = failing && summary.rail === 'STRIPE';
  const day = formatDay(summary.date, options);
  const text = stripeFailing
    ? 'Payment failing'
    : failing
      ? day
        ? `Payment failing · ${day}`
        : 'Payment failing (date unknown)'
      : dated(cancelling ? 'Cancels' : 'Renews', summary.date, options);
  const offer = summary.offerKind ? (summary.offerKind === 'free_trial' ? ' · trial' : ' · intro') : '';
  const sandbox = summary.environment === 'Sandbox' ? ' · sandbox' : '';
  const label: Labelled = {
    text: `${text}${offer}${sandbox}`,
    tone: cancelling || failing ? 'warning' : 'neutral',
  };
  // No date in the text, no instant behind it.
  return stripeFailing ? label : withTitle(label, summary.date);
}

const OFFER_WORDING: Record<TeamsOfferKind, { name: string; ends: string }> = {
  free_trial: { name: 'Free trial', ends: 'ends' },
  pay_as_you_go: { name: 'Intro price (pay as you go)', ends: 'period ends' },
  pay_up_front: { name: 'Intro price (paid up front)', ends: 'ends' },
  introductory: { name: 'Intro offer', ends: 'ends' },
};

// An offer kind a newer API adds: said generically, never printed raw.
const UNKNOWN_OFFER_WORDING = { name: 'Offer', ends: 'ends' };

export function offerLabel(
  offer: TeamsOffer | null | undefined,
  options: FormatOptions = {},
): string | null {
  if (!offer) return null;
  const wording = Object.hasOwn(OFFER_WORDING, offer.kind)
    ? OFFER_WORDING[offer.kind]
    : UNKNOWN_OFFER_WORDING;
  const day = formatDay(offer.endsAt, options);
  return day ? `${wording.name}, ${wording.ends} ${day}` : wording.name;
}

/** "$49.99": the API sends major units and an upper-case currency code. */
export function priceLabel(
  price: TeamsPrice | null | undefined,
  options: FormatOptions = {},
): string | null {
  if (!price || typeof price.amount !== 'number' || !Number.isFinite(price.amount)) {
    return null;
  }
  const currency = typeof price.currency === 'string' ? price.currency : '';
  try {
    return plain(
      new Intl.NumberFormat(options.locale, { style: 'currency', currency }).format(price.amount),
    );
  } catch {
    // Intl rejects a code that is not three letters: show what the API sent.
    const amount = price.amount.toFixed(2);
    return currency ? `${amount} ${currency}` : amount;
  }
}

// Stripe's cancellation_details feedback and reason tokens.
const CANCEL_REASONS: Record<string, string> = {
  too_expensive: 'Too expensive',
  missing_features: 'Missing features',
  switched_service: 'Switched service',
  unused: 'Not used enough',
  customer_service: 'Customer service',
  too_complex: 'Too complex',
  low_quality: 'Quality',
  other: 'Other',
  cancellation_requested: 'Customer cancelled',
  payment_failed: 'Payment failed',
  payment_disputed: 'Payment disputed',
};

/** A token this build does not know is shown as it came; nothing is null. */
export function cancelReasonLabel(token: string | null | undefined): string | null {
  if (!token) return null;
  return Object.hasOwn(CANCEL_REASONS, token) ? CANCEL_REASONS[token]! : token;
}

export function railLabel(rail: string | null | undefined): string | null {
  if (rail === 'APPLE') return 'Apple';
  if (rail === 'STRIPE') return 'Stripe';
  return null;
}

export function planLabel(plan: string | null | undefined): string {
  switch (plan) {
    case 'monthly':
      return 'Monthly';
    case 'yearly':
      return 'Yearly';
    case 'multilocation':
      return 'Multi-location';
    default:
      return 'Unknown plan';
  }
}

// Apple's billing grace period and Stripe's past_due: the card is failing, and
// the subscription still entitles the team for now.
const PAYMENT_ISSUE_STATUSES = new Set(['grace_period', 'past_due']);

// A status that needs no explanation: the Billing card has no Status row for it.
const HEALTHY_STATUSES = new Set(['active', 'trialing', 'in_trial']);

const STATUS_WORDING: Record<string, string> = {
  grace_period: 'Billing grace period',
  past_due: 'Past due',
  unpaid: 'Unpaid',
  incomplete: 'First payment incomplete',
  paused: 'Paused',
};

/**
 * The pill at the head of each Billing block. A renewing subscription whose
 * payment is failing is a warning rather than "Renews": in Apple's grace
 * period its date is when access stops, and Stripe's is a period end it has
 * already rolled forward and will not give if the card stays bad.
 */
export function stateLabel(
  subscription: TeamsSubscriptionDetail,
  options: FormatOptions = {},
): Labelled {
  switch (subscription.state) {
    case 'renewing':
      if (subscription.status && PAYMENT_ISSUE_STATUSES.has(subscription.status)) {
        return subscription.rail === 'APPLE'
          ? withTitle(
              { text: dated('Payment failing, access until', subscription.renewsAt, options), tone: 'warning' },
              subscription.renewsAt,
            )
          : { text: 'Payment failing', tone: 'warning' };
      }
      return withTitle(
        { text: dated('Renews', subscription.renewsAt, options), tone: 'neutral' },
        subscription.renewsAt,
      );
    case 'cancelling':
      return withTitle(
        { text: dated('Cancels', subscription.accessUntil, options), tone: 'warning' },
        subscription.accessUntil,
      );
    case 'ended':
      return withTitle(
        { text: dated('Ended', subscription.accessUntil, options), tone: 'neutral' },
        subscription.accessUntil,
      );
    default:
      return { text: 'State unknown', tone: 'neutral' };
  }
}

export interface BillingRow {
  label: string;
  value: string;
  /** The full UTC instant behind a date row (a hover); absent on the other rows. */
  title?: string;
}

/** The rows under a Billing block's head, in order; a null value has no row. */
export function billingRows(
  subscription: TeamsSubscriptionDetail,
  options: FormatOptions = {},
): BillingRow[] {
  const rows: BillingRow[] = [];
  const add = (label: string, value: string | null | undefined, instant?: string | null): void => {
    if (!value) return;
    const title = utcInstant(instant);
    rows.push(title ? { label, value, title } : { label, value });
  };

  // Why the card is not simply renewing, in words, when the rail's own status
  // says it is not healthy; any status this build has no wording for is shown as it came.
  const status = subscription.status;
  if (status && !HEALTHY_STATUSES.has(status)) {
    add('Status', Object.hasOwn(STATUS_WORDING, status) ? STATUS_WORDING[status] : status);
  }
  // Apple's price is what the transaction charged. Stripe's is the plan's list
  // unit price: it knows nothing of a promotion code, and a team with several
  // venues pays it once for each, so the label says so and the count follows.
  const price = priceLabel(subscription.price, options);
  if (subscription.rail === 'STRIPE') {
    const quantity = subscription.quantity;
    const venues = typeof quantity === 'number' && Number.isFinite(quantity) && quantity > 1 ? quantity : null;
    add('List price', price && venues ? `${price} × ${venues}` : price);
  } else {
    add('Price', price);
  }
  add('Offer', offerLabel(subscription.offer, options), subscription.offer?.endsAt);
  add('Auto-renew turned off', formatDayTime(subscription.cancelledAt, options), subscription.cancelledAt);
  // Our own account-deletion cancel is not a customer's choice. Stripe still
  // gives it the generic "cancellation_requested" token and our note rides in
  // the comment, so the source decides, and the comment (ours, not the
  // customer's) is not shown as theirs. Only an ended subscription means the
  // account is gone: while it is cancelling, the deletion is scheduled and the
  // owner can still restore it.
  const byDeletion = subscription.cancelSource === 'account_deletion';
  add(
    'Reason',
    byDeletion
      ? subscription.state === 'ended'
        ? 'Account deleted'
        : 'Account deletion scheduled'
      : cancelReasonLabel(subscription.cancelReason),
  );
  const comment =
    !byDeletion && typeof subscription.cancelComment === 'string'
      ? subscription.cancelComment.trim()
      : '';
  add('Comment', comment);
  add('Access until', formatDay(subscription.accessUntil, options), subscription.accessUntil);
  add('Subscriber', subscription.subscriberEmail);
  const rail = railLabel(subscription.rail);
  add(
    rail ? `Last checked with ${rail}` : 'Last checked',
    formatDayTime(subscription.lastVerifiedAt, options),
    subscription.lastVerifiedAt,
  );
  return rows;
}

export interface BillingBlock {
  rail: string;
  plan: string;
  sandbox: boolean;
  state: Labelled;
  rows: BillingRow[];
}

export type BillingCard =
  | { kind: 'empty'; text: string }
  | { kind: 'subscriptions'; blocks: BillingBlock[] };

/**
 * What the team page's Billing section shows. Null means "skip the section":
 * the API did not send `billing` at all (it predates the field), and saying
 * "No paid subscription" then would be a claim nobody checked.
 */
export function billingCard(
  billing: TeamsBilling | null | undefined,
  options: FormatOptions = {},
): BillingCard | null {
  if (!billing || !Array.isArray(billing.subscriptions)) return null;
  const subscriptions = billing.subscriptions.filter(
    (s): s is TeamsSubscriptionDetail => !!s && typeof s === 'object',
  );
  if (subscriptions.length === 0) return { kind: 'empty', text: NO_PAID_SUBSCRIPTION };
  return {
    kind: 'subscriptions',
    blocks: subscriptions.map((s) => ({
      rail: railLabel(s.rail) ?? 'Billing',
      plan: planLabel(s.plan),
      sandbox: s.environment === 'Sandbox',
      state: stateLabel(s, options),
      rows: billingRows(s, options),
    })),
  };
}

function count(value: unknown): number {
  return typeof value === 'number' && Number.isFinite(value) ? value : 0;
}

/** "Refreshed: Apple 12, Stripe 3", plus ", 2 failed" when any failed. */
export function refreshSummary(result: RefreshResult | null | undefined): string {
  const failed = count(result?.apple?.failed) + count(result?.stripe?.failed);
  const text = `Refreshed: Apple ${count(result?.apple?.refreshed)}, Stripe ${count(result?.stripe?.refreshed)}`;
  return failed > 0 ? `${text}, ${failed} failed` : text;
}

/** A second refresh while one runs is a 409; anything else keeps the API's words. */
export function refreshFailureMessage(status: number | undefined, message: string): string {
  if (status === 409) return 'A refresh is already running.';
  return message.trim() ? message : 'The refresh failed.';
}
